import { useCallback, useEffect, useRef, useState } from 'react';
import { storage } from '../../utils/platform';
import { useCurrentRestaurant } from '../contexts/CurrentRestaurantContext';
import { API_BASE } from '../../config/api';

const CACHE_KEY = 'revyo_insights_cache';
const CACHE_TTL_MS = 60 * 60 * 1000;       // 1h
const COOLDOWN_MS = 5 * 60 * 1000;          // 5 min entre 2 régénérations
const CACHE_VERSION = 1;

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CACHE_VERSION) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function writeCache(payload) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ...payload, version: CACHE_VERSION }));
  } catch (_) {}
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, options);
  let payload = null;
  try { payload = await res.json(); } catch (_) {}
  if (!res.ok) {
    const err = new Error(payload?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.payload = payload;
    throw err;
  }
  return payload;
}

export function useInsights({ restaurantId: restaurantIdOverride } = {}) {
  // Vague 10 : si pas d'override (ex MobileInsights qui passe le restaurantSnapshot.id),
  // on lit le currentRestaurantId du context. Sinon override prime (rétrocompat).
  const { currentRestaurantId } = useCurrentRestaurant();
  const restaurantId = restaurantIdOverride || currentRestaurantId;

  const [data, setData] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(null);
  const [cooldownRemainingMs, setCooldownRemainingMs] = useState(0);
  const cancelRef = useRef(false);

  const updateCooldown = useCallback((from) => {
    if (!from) { setCooldownRemainingMs(0); return; }
    const remaining = Math.max(0, COOLDOWN_MS - (Date.now() - from));
    setCooldownRemainingMs(remaining);
  }, []);

  // Tick toutes les secondes pour mettre à jour le cooldown affiché
  useEffect(() => {
    if (!fetchedAt) return undefined;
    updateCooldown(fetchedAt);
    const id = setInterval(() => updateCooldown(fetchedAt), 1000);
    return () => clearInterval(id);
  }, [fetchedAt, updateCooldown]);

  const doFetch = useCallback(async () => {
    const token = await storage.get('token');
    if (!token) throw new Error('NO_TOKEN');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(restaurantId ? { 'X-Restaurant-Id': restaurantId } : {}),
    };
    return fetchJson(`${API_BASE}/api/dashboard/insights`, { headers });
  }, [restaurantId]);

  const fetchInsights = useCallback(async ({ force = false } = {}) => {
    setError(null);
    // Cache valide ?
    if (!force) {
      const cached = readCache();
      if (cached
        && cached.fetchedAt
        && (Date.now() - cached.fetchedAt) < CACHE_TTL_MS
        && (!restaurantId || cached.restaurantId === restaurantId)
      ) {
        setData(cached.data);
        setFetchedAt(cached.fetchedAt);
        setLoading(false);
        return cached.data;
      }
    }

    setLoading(true);
    try {
      const fresh = await doFetch();
      if (cancelRef.current) return null;
      const now = Date.now();
      setData(fresh);
      setFetchedAt(now);
      writeCache({ data: fresh, fetchedAt: now, restaurantId: restaurantId || null });
      return fresh;
    } catch (e) {
      console.error('[useInsights] fetch failed:', e);
      if (!cancelRef.current) setError(e.message || 'Erreur de chargement');
      throw e;
    } finally {
      if (!cancelRef.current) setLoading(false);
    }
  }, [doFetch, restaurantId]);

  const regenerate = useCallback(async () => {
    // Cooldown : on refuse silencieusement
    if (fetchedAt && (Date.now() - fetchedAt) < COOLDOWN_MS) {
      return { ok: false, reason: 'cooldown' };
    }
    setRegenerating(true);
    try {
      await fetchInsights({ force: true });
      return { ok: true };
    } catch (e) {
      return { ok: false, reason: 'error', error: e.message };
    } finally {
      if (!cancelRef.current) setRegenerating(false);
    }
  }, [fetchInsights, fetchedAt]);

  useEffect(() => {
    cancelRef.current = false;
    fetchInsights({ force: false }).catch(() => {});
    return () => { cancelRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data,
    loading,
    regenerating,
    error,
    fetchedAt,
    cooldownRemainingMs,
    fetchInsights,
    regenerate,
  };
}
